// The ONLY module that imports Phaser (IMPLEMENTATION §2.9, risk register "Phaser 4 API drift").
// Every Phaser call the game makes goes through a small named function here, so an API change (or a
// PixiJS fallback) is a one-file edit. Each call below was checked against node_modules/phaser/types/phaser.d.ts
// (Phaser 4.2.1) and is exercised by the `?spike=1` probe.
import * as Phaser from 'phaser';
import type { Rgba } from '../Renderer.ts';

export type PGame = Phaser.Game;
export type PScene = Phaser.Scene;
export type PImage = Phaser.GameObjects.Image;
export type PCanvasTexture = Phaser.Textures.CanvasTexture;
export type PEmitter = Phaser.GameObjects.Particles.ParticleEmitter;

export type MipmapFilter =
  | 'NEAREST'
  | 'LINEAR'
  | 'NEAREST_MIPMAP_NEAREST'
  | 'LINEAR_MIPMAP_NEAREST'
  | 'NEAREST_MIPMAP_LINEAR'
  | 'LINEAR_MIPMAP_LINEAR';

export interface SceneHooks {
  key: string;
  /** Started at boot (default true); an inactive scene is started later with `startScene`. */
  active?: boolean;
  create(scene: PScene): void;
  update?(scene: PScene, timeMs: number): void;
}

export interface GameOptions {
  parent: HTMLElement;
  /** Canvas CSS size (full layout viewport). */
  cssW: number;
  cssH: number;
  dprEff: number;
  background: string;
  /** `render.mipmapFilter`; null = no mipmaps (Phaser default). */
  mipmapFilter: MipmapFilter | null;
  scenes: SceneHooks[];
}

function makeScene(hooks: SceneHooks, onCreate: () => void): Phaser.Scene {
  class HookScene extends Phaser.Scene {
    constructor() {
      super({ key: hooks.key, active: hooks.active ?? true });
    }
    create(): void {
      hooks.create(this);
      onCreate();
    }
    override update(time: number): void {
      hooks.update?.(this, time);
    }
  }
  return new HookScene();
}

/** Backing store = round(css·dprEff); CSS size = css (scale.zoom = 1/dprEff). */
export function backingSize(cssW: number, cssH: number, dprEff: number): { w: number; h: number } {
  return { w: Math.max(1, Math.round(cssW * dprEff)), h: Math.max(1, Math.round(cssH * dprEff)) };
}

/** Creates the game (WEBGL only, Phaser input/audio off) and resolves once every scene has run create(). */
export function createPhaserGame(o: GameOptions): Promise<PGame> {
  const { w, h } = backingSize(o.cssW, o.cssH, o.dprEff);
  return new Promise<PGame>((resolve, reject) => {
    let pending = o.scenes.length;
    let game: PGame | null = null;
    const created = (): void => {
      pending -= 1;
      if (pending === 0 && game) {
        const g = game;
        g.events.once(Phaser.Core.Events.POST_RENDER, () => resolve(g));
      }
    };
    try {
      const render: Phaser.Types.Core.RenderConfig = {
        antialias: true,
        roundPixels: false,
        powerPreference: 'default',
      };
      if (o.mipmapFilter) render.mipmapFilter = o.mipmapFilter;
      game = new Phaser.Game({
        type: Phaser.WEBGL,
        parent: o.parent,
        backgroundColor: o.background,
        banner: false,
        disableContextMenu: true,
        audio: { noAudio: true },
        input: { keyboard: false, mouse: false, touch: false, gamepad: false },
        render,
        scale: {
          mode: Phaser.Scale.NONE,
          width: w,
          height: h,
          zoom: 1 / o.dprEff,
          autoRound: false,
          expandParent: false,
        },
        scene: o.scenes.map((s) => makeScene(s, created)),
      });
    } catch (err) {
      reject(err);
    }
  });
}

/** Stops `scene` and starts the scene `key` (BootScene → FactoryScene). */
export function startScene(scene: PScene, key: string): void {
  scene.scene.start(key);
}

export function destroyGame(game: PGame): void {
  game.destroy(true, false);
}

export function gameCanvas(game: PGame): HTMLCanvasElement {
  return game.canvas;
}

/** True when the game runs on the WebGL renderer with a live context. */
export function isWebGL(game: PGame): boolean {
  if (game.config.renderType !== Phaser.WEBGL) return false;
  const r = game.renderer;
  if (!(r instanceof Phaser.Renderer.WebGL.WebGLRenderer)) return false;
  return !r.gl.isContextLost();
}

function glOf(game: PGame): WebGLRenderingContext | null {
  const r = game.renderer;
  return r instanceof Phaser.Renderer.WebGL.WebGLRenderer ? r.gl : null;
}

export function glInfo(
  game: PGame,
): { version: string; renderer: string; maxTextureSize: number } | null {
  const gl = glOf(game);
  if (!gl) return null;
  const dbg = gl.getExtension('WEBGL_debug_renderer_info');
  const renderer = dbg
    ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)
    : gl.getParameter(gl.RENDERER);
  return {
    version: String(gl.getParameter(gl.VERSION)),
    renderer: String(renderer),
    maxTextureSize: Number(gl.getParameter(gl.MAX_TEXTURE_SIZE)),
  };
}

/** Relayout (§2.9): scale.resize(backing) + scale.setZoom(1/dprEff), then the canvas CSS size explicitly. */
export function resizeGame(game: PGame, cssW: number, cssH: number, dprEff: number): void {
  const { w, h } = backingSize(cssW, cssH, dprEff);
  game.scale.setZoom(1 / dprEff);
  game.scale.resize(w, h);
  const c = game.canvas;
  c.style.width = `${cssW}px`;
  c.style.height = `${cssH}px`;
}

/** M7 Low quality: the game loop's frame-rate limit (TimeStep.setFPSLimit; 0 = none). */
export function setFpsLimit(game: PGame, fps: number): void {
  game.loop.setFPSLimit(fps);
}

export function scaleZoom(game: PGame): number {
  return game.scale.zoom;
}

// ---- textures -------------------------------------------------------------------------------------------

export function addCanvasTexture(
  scene: PScene,
  key: string,
  canvas: HTMLCanvasElement,
): PCanvasTexture | null {
  return scene.textures.addCanvas(key, canvas);
}

export function hasTexture(scene: PScene, key: string): boolean {
  return scene.textures.exists(key);
}

/**
 * Adds a named frame (sub-rectangle) to a canvas texture. With `trim` (M8: the atlas packs frames trimmed
 * to their alpha bounds) the frame keeps its untrimmed source size `sw`×`sh` and draws its w×h pixels at
 * (`tx`, `ty`) inside it (Frame.setTrim, TexturePacker semantics): display size, origin and crop all work
 * in the untrimmed source box.
 */
export function addFrame(
  tex: PCanvasTexture,
  name: string,
  x: number,
  y: number,
  w: number,
  h: number,
  trim?: { sw: number; sh: number; tx: number; ty: number },
): boolean {
  const f = tex.add(name, 0, x, y, w, h);
  if (f === null || !tex.has(name)) return false;
  if (trim && (trim.tx !== 0 || trim.ty !== 0 || trim.sw !== w || trim.sh !== h))
    f.setTrim(trim.sw, trim.sh, trim.tx, trim.ty, w, h);
  return true;
}

export function frameSize(tex: PCanvasTexture, name: string): { w: number; h: number } | null {
  if (!tex.has(name)) return null;
  const f = tex.get(name);
  return { w: f.width, h: f.height };
}

/** Whether the texture's GL source uses a mipmap min filter, and is power-of-two. */
export function textureMipmapInfo(tex: PCanvasTexture): {
  isMipmap: boolean;
  pot: boolean;
  w: number;
  h: number;
} {
  const src = tex.source[0];
  if (!src) return { isMipmap: false, pot: false, w: 0, h: 0 };
  const pot = src.isPowerOf2;
  const gt = src.glTexture as { isMipmap?: () => boolean } | null;
  const isMipmap = gt !== null && typeof gt.isMipmap === 'function' ? gt.isMipmap() : false;
  return { isMipmap, pot, w: src.width, h: src.height };
}

/** The renderer-level mipmap setting (GL enum, 0 when unset). */
export function rendererMipmapFilter(game: PGame): number {
  const r = game.renderer;
  return r instanceof Phaser.Renderer.WebGL.WebGLRenderer ? Number(r.mipmapFilter ?? 0) : 0;
}

// ---- game objects ---------------------------------------------------------------------------------------

export function addImage(scene: PScene, x: number, y: number, key: string, frame: string): PImage {
  return scene.add.image(x, y, key, frame);
}

export function imageFrameName(img: PImage): string {
  return String(img.frame.name);
}

export function placeImage(
  img: PImage,
  x: number,
  y: number,
  w: number,
  h: number,
  depth: number,
): void {
  img.setPosition(x, y).setDisplaySize(w, h).setDepth(depth);
}

export function setOriginTopLeft(img: PImage): void {
  img.setOrigin(0, 0);
}

export function setOriginAt(img: PImage, ox: number, oy: number): void {
  img.setOrigin(ox, oy);
}

/** Switches texture page and frame (multi-page atlas). */
export function setTextureFrame(img: PImage, key: string, frame: string): void {
  img.setTexture(key, frame);
}

export function setPos(img: PImage, x: number, y: number): void {
  img.setPosition(x, y);
}

export function setDisplaySize(img: PImage, w: number, h: number): void {
  img.setDisplaySize(w, h);
}

/** Rotation in degrees, clockwise (about the image origin). */
export function setAngle(img: PImage, deg: number): void {
  img.setAngle(deg);
}

/** Mirror in the image's own x (before its rotation; trimmed frames mirror about their source box). */
export function setFlipX(img: PImage, on: boolean): void {
  img.setFlipX(on);
}

export function setDepth(img: PImage, depth: number): void {
  img.setDepth(depth);
}

/** Removes a crop (full frame again). */
export function clearCrop(img: PImage): void {
  img.setCrop();
}

/** Number of game objects of a Phaser type ('Image', 'Text', 'Graphics', …) on the scene's display list. */
export function countObjects(scene: PScene, type: string): number {
  let n = 0;
  for (const o of scene.children.list) if (o.type === type) n++;
  return n;
}

export function setVisible(img: PImage, on: boolean): void {
  img.setVisible(on);
}

/** Visibility and position as the game object holds them (render diagnostics). */
export function imageState(img: PImage): { visible: boolean; x: number; y: number } {
  return { visible: img.visible, x: img.x, y: img.y };
}

export function setAlpha(img: PImage, a: number): void {
  img.setAlpha(a);
}

export function alphaOf(img: PImage): number {
  return img.alpha;
}

export function setScale(img: PImage, s: number): void {
  img.setScale(s);
}

export function destroyObject(obj: { destroy(): void }): void {
  obj.destroy();
}

/** Phaser 4 tint: colour + mode (setTintFill was removed in v4). */
export function tintFill(img: PImage, color: number): void {
  img.setTint(color).setTintMode(Phaser.TintModes.FILL);
}

export function tintMultiply(img: PImage, color: number): void {
  img.setTint(color).setTintMode(Phaser.TintModes.MULTIPLY);
}

/** Additive blending (M10 flashes, sparks, glows) or back to normal. */
export function setBlendAdd(img: PImage, add: boolean): void {
  img.setBlendMode(add ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL);
}

export function clearTint(img: PImage): void {
  img.clearTint();
}

export function tintState(img: PImage): { color: number; fill: boolean } {
  return { color: img.tintTopLeft, fill: img.tintMode === Phaser.TintModes.FILL };
}

/** Crop in frame pixels (progress bars in §2.9). */
export function crop(img: PImage, x: number, y: number, w: number, h: number): void {
  img.setCrop(x, y, w, h);
}

export function isCropped(img: PImage): boolean {
  return img.isCropped;
}

// ---- tweens / particles ---------------------------------------------------------------------------------

/** Tweens `props` on `target`; resolves on complete. */
export function tweenTo(
  scene: PScene,
  target: object,
  props: Record<string, number>,
  durationMs: number,
  ease?: string,
): Promise<void> {
  return new Promise<void>((resolve) => {
    scene.tweens.add({
      targets: target,
      ...props,
      duration: durationMs,
      ...(ease !== undefined ? { ease } : {}),
      onComplete: () => resolve(),
      onStop: () => resolve(),
    });
  });
}

/** Stops every tween of `target` (its values stay where they are). */
export function killTweensOf(scene: PScene, target: object): void {
  scene.tweens.killTweensOf(target);
}

export function killAllTweens(scene: PScene): void {
  scene.tweens.killAll();
}

/** Tween clock scale (0 freezes every tween: `__gm.render.freezeAt`). */
export function setTweenTimeScale(scene: PScene, k: number): void {
  scene.tweens.timeScale = k;
}

export function activeTweenCount(scene: PScene): number {
  return scene.tweens.getTweens().length;
}

/** Burst emitter settings (M10 sources them from juice.json; the spike probe passes its own). */
export interface BurstConfig {
  lifespanMs: number;
  speedMin: number;
  speedMax: number;
  scaleStart: number;
  scaleEnd: number;
}

export function addBurstEmitter(
  scene: PScene,
  key: string,
  frame: string,
  depth: number,
  cfg: BurstConfig,
): PEmitter {
  const e = scene.add.particles(0, 0, key, {
    frame,
    lifespan: cfg.lifespanMs,
    speed: { min: cfg.speedMin, max: cfg.speedMax },
    scale: { start: cfg.scaleStart, end: cfg.scaleEnd },
    emitting: false,
  });
  e.setDepth(depth);
  return e;
}

export function explode(e: PEmitter, count: number, x: number, y: number): void {
  e.explode(count, x, y);
}

export function aliveParticles(e: PEmitter): number {
  return e.getAliveParticleCount();
}

// ---- camera ---------------------------------------------------------------------------------------------

/** Main camera with origin (0,0): screen = zoom·(world − scroll). */
export function setCamera(scene: PScene, zoom: number, scrollX: number, scrollY: number): void {
  const cam = scene.cameras.main;
  cam.setOrigin(0, 0);
  cam.setZoom(zoom);
  cam.setScroll(scrollX, scrollY);
}

export function cameraState(scene: PScene): { zoom: number; scrollX: number; scrollY: number } {
  const cam = scene.cameras.main;
  return { zoom: cam.zoom, scrollX: cam.scrollX, scrollY: cam.scrollY };
}

/** Canvas px → world, through the camera matrix (checks zoom + scroll are really applied). */
export function screenToWorld(scene: PScene, x: number, y: number): { x: number; y: number } {
  const cam = scene.cameras.main;
  cam.preRender();
  const p = cam.getWorldPoint(x, y);
  return { x: p.x, y: p.y };
}

export function resizeCamera(scene: PScene, w: number, h: number): void {
  scene.cameras.main.setSize(w, h);
}

// ---- snapshots (spike probe only) -----------------------------------------------------------------------

export type { Rgba };

/** Reads one canvas pixel after the next render (top-left origin, backing-store px). */
export function snapshotPixel(game: PGame, x: number, y: number): Promise<Rgba | null> {
  return new Promise<Rgba | null>((resolve) => {
    const r = game.renderer;
    if (!(r instanceof Phaser.Renderer.WebGL.WebGLRenderer)) {
      resolve(null);
      return;
    }
    r.snapshotPixel(Math.round(x), Math.round(y), (c) => {
      if (c instanceof Phaser.Display.Color)
        resolve({ r: c.red, g: c.green, b: c.blue, a: c.alpha });
      else resolve(null);
    });
  });
}
